import React from 'react';

import Heading from '@react/react-spectrum/Heading';
import Button from '@react/react-spectrum/Button';
import Radio from '@react/react-spectrum/Radio';
import { Grid } from '@material-ui/core';
import TextField from '@react/react-spectrum/Textfield';
import Select from '@react/react-spectrum/Select';
import { TabView, Tab } from '@react/react-spectrum/TabView';

import './ManageBannerPage.css';
import { getActiveBannerForManage, createBanner, extendBanner, deleteBanner } from '../../../providers/BannerAPI';
import { BannerObject, BannerSeverity } from '../../../models/banner/BannerObject';
import InformationDialog from '../../../utils/Dialogs/InformationDialog';
import ConfirmationDialog from '../../../utils/Dialogs/ConfirmationDialog';
import TemplatePickerDialog, { MessageTemplate } from '../../../utils/Dialogs/TemplatePickerDialog';
import ManageBannerAdminsPage from './ManageBannerAdminsPage';
import BannerContent from '../../../components/MaintenanceBanner/BannerContent';
import SeverityBadge from '../../../components/SeverityBadge/SeverityBadge';

const SEVERITY_OPTIONS = [
  { label: 'Info', value: 'INFO' },
  { label: 'Warning', value: 'WARNING' },
  { label: 'Critical', value: 'CRITICAL' }
];

const MESSAGE_TEMPLATES: MessageTemplate[] = [
  {
    label: 'Customer Management Service Disruption',
    severity: 'CRITICAL',
    message: `We're currently experiencing an issue affecting our customer management services. During this time, requests to create or update customer accounts and related information may fail or may not be processed successfully.

Our teams are working to restore normal service. Once the issue is resolved, we'll update this banner to blue to confirm that customer management services are operating normally again.

Thank you for your patience.`
  },
  {
    label: 'Subscription Management Service Disruption',
    severity: 'CRITICAL',
    message: `We're currently experiencing an issue affecting our subscription management services. During this time, requests to create, update, or manage subscriptions may fail or may not be processed successfully.

Our teams are working to restore normal service. Once the issue is resolved, we'll update this banner to blue to confirm that subscription management services are operating normally again.

Thank you for your patience.`
  },
  {
    label: 'Ordering Service Disruption',
    severity: 'CRITICAL',
    message: `We're currently experiencing an outage affecting our ordering systems. Orders submitted during the next few hours may fail or may not be processed successfully.
Our teams are working to restore normal service. Once the issue is resolved, we'll update this banner to blue to confirm that ordering services are operating normally again.

Thank you for your patience.`
  },
  {
    label: 'Ordering Service — Recent Incident',
    severity: 'INFO',
    message: `We recently experienced an issue affecting our ordering services between [Start Time] and [End Time] [Time Zone]. The issue has been resolved and ordering services are now operating normally.

Orders submitted during the affected period may not have been processed successfully or may contain incomplete information. We recommend reviewing any orders created during this time and resubmitting them if necessary.

Thank you for your patience.`
  },
  {
    label: 'Customer Management — Recent Incident',
    severity: 'INFO',
    message: `We recently experienced an issue affecting our customer management services between [Start Time] and [End Time] [Time Zone]. The issue has been resolved and customer management services are now operating normally.

Customer records created or updated during the affected period may not have been processed successfully or may contain incomplete information. We recommend reviewing records created or modified during this time and taking corrective action if necessary.

Thank you for your patience.`
  },
  {
    label: 'Subscription Management — Recent Incident',
    severity: 'INFO',
    message: `We recently experienced an issue affecting our subscription management services between [Start Time] and [End Time] [Time Zone]. The issue has been resolved and subscription management services are now operating normally.

Subscriptions created or updated during the affected period may not have been processed successfully or may not reflect the intended changes. We recommend reviewing subscription activity from this period and retrying any unsuccessful requests if necessary.

Thank you for your patience.`
  }
];

type ExpiryMode = 'DURATION' | 'CUSTOM';
type DurationUnit = 'MINUTES' | 'HOURS';

const DURATION_UNIT_OPTIONS = [
  { label: 'Minutes', value: 'MINUTES' },
  { label: 'Hours', value: 'HOURS' }
];

function durationToExpiryDate(amount: string, unit: DurationUnit, baseTime: number): string {
  const parsedAmount = Number(amount);
  if (!parsedAmount || parsedAmount <= 0) {
    return '';
  }
  const unitMs = unit === 'MINUTES' ? 60 * 1000 : 60 * 60 * 1000;
  return new Date(baseTime + parsedAmount * unitMs).toISOString();
}

interface ExpiryPickerState {
  mode: ExpiryMode;
  setMode: (mode: ExpiryMode) => void;
  durationAmount: string;
  setDurationAmount: (value: string) => void;
  durationUnit: DurationUnit;
  setDurationUnit: (unit: DurationUnit) => void;
  customDate: string;
  setCustomDate: (value: string) => void;
  idPrefix: string;
}

function isBannerExpired(banner: BannerObject, now: number): boolean {
  return Date.parse(banner.expiryDate) < now;
}

function formatTimeRemaining(expiryDate: string, now: number): string | null {
  const diffMs = Date.parse(expiryDate) - now;
  if (diffMs <= 0) {
    return null;
  }
  const totalMinutes = Math.floor(diffMs / 60000);
  if (totalMinutes < 1) {
    return '<1m remaining';
  }
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (days > 0 || hours > 0) {
    parts.push(`${hours}h`);
  }
  parts.push(`${minutes}m`);

  return `${parts.join(' ')} remaining`;
}

const MAX_TITLE_LENGTH = 100;
const MAX_MESSAGE_LENGTH = 1000;
const TEMPLATE_TOKEN_PATTERN = /\[(?:Start Time|End Time|Time Zone)\]/;

function describeDisabledPublishReason(hasTitle: boolean, hasMessage: boolean, hasExpiryDate: boolean): string {
  const missingTitleOrMessage = !hasTitle || !hasMessage;
  if (missingTitleOrMessage && !hasExpiryDate) {
    return 'Enter a title, message, and expiry to enable these buttons.';
  }
  if (missingTitleOrMessage) {
    return 'Enter a title and message to enable these buttons.';
  }
  return 'Set an expiry to enable Publish.';
}

function resolveExpiryDate(state: ExpiryPickerState, baseTime: number): string {
  return state.mode === 'DURATION'
    ? durationToExpiryDate(state.durationAmount, state.durationUnit, baseTime)
    : state.customDate;
}

function ExpiryPicker({
  mode,
  setMode,
  durationAmount,
  setDurationAmount,
  durationUnit,
  setDurationUnit,
  customDate,
  setCustomDate,
  idPrefix
}: Readonly<ExpiryPickerState>): React.ReactElement {
  return (
    <div className="expiry-picker">
      <div className="expiry-picker-row">
        <Radio
          id={`${idPrefix}-duration-radio`}
          checked={mode === 'DURATION'}
          onChange={() => setMode('DURATION')}
          label="Show banner for"
        />
        <TextField
          type="number"
          min="1"
          className="expiry-duration-amount"
          value={durationAmount}
          disabled={mode !== 'DURATION'}
          placeholder="e.g. 30"
          onChange={setDurationAmount}
        />
        <Select
          aria-label="Duration unit"
          className="expiry-duration-unit"
          disabled={mode !== 'DURATION'}
          value={durationUnit}
          options={DURATION_UNIT_OPTIONS}
          onSelect={(value: DurationUnit) => setDurationUnit(value)}
        />
      </div>
      <div className="expiry-picker-row">
        <Radio
          id={`${idPrefix}-custom-radio`}
          checked={mode === 'CUSTOM'}
          onChange={() => setMode('CUSTOM')}
          label="Custom expiry date"
        />
        <TextField
          className="expiry-custom-date"
          value={customDate}
          disabled={mode !== 'CUSTOM'}
          placeholder="ISO-8601, e.g. 2026-08-10T18:00:00Z"
          onChange={setCustomDate}
        />
      </div>
    </div>
  );
}

function PublishBannerPage(): React.ReactElement {
  const [activeBanner, setActiveBanner] = React.useState<BannerObject | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [severity, setSeverity] = React.useState<BannerSeverity>('WARNING');
  const [resultDialog, setResultDialog] = React.useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = React.useState(false);
  const [showPreview, setShowPreview] = React.useState(false);

  const [expiryMode, setExpiryMode] = React.useState<ExpiryMode>('DURATION');
  const [durationAmount, setDurationAmount] = React.useState('');
  const [durationUnit, setDurationUnit] = React.useState<DurationUnit>('MINUTES');
  const [customDate, setCustomDate] = React.useState('');
  const expiryDate = resolveExpiryDate(
    {
      mode: expiryMode,
      setMode: setExpiryMode,
      durationAmount,
      setDurationAmount,
      durationUnit,
      setDurationUnit,
      customDate,
      setCustomDate,
      idPrefix: 'publish'
    },
    Date.now()
  );

  const [extendMode, setExtendMode] = React.useState<ExpiryMode>('DURATION');
  const [extendDurationAmount, setExtendDurationAmount] = React.useState('');
  const [extendDurationUnit, setExtendDurationUnit] = React.useState<DurationUnit>('MINUTES');
  const [extendCustomDate, setExtendCustomDate] = React.useState('');
  const extendBaseTime = activeBanner
    ? Math.max(Date.now(), Date.parse(activeBanner.expiryDate))
    : Date.now();
  const extendExpiryDate = resolveExpiryDate(
    {
      mode: extendMode,
      setMode: setExtendMode,
      durationAmount: extendDurationAmount,
      setDurationAmount: setExtendDurationAmount,
      durationUnit: extendDurationUnit,
      setDurationUnit: setExtendDurationUnit,
      customDate: extendCustomDate,
      setCustomDate: setExtendCustomDate,
      idPrefix: 'extend'
    },
    extendBaseTime
  );

  const refreshActiveBanner = () => {
    getActiveBannerForManage().then(setActiveBanner);
  };

  React.useEffect(() => {
    refreshActiveBanner();
  }, []);

  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    if (!activeBanner) {
      return undefined;
    }
    const msUntilExpiry = Date.parse(activeBanner.expiryDate) - Date.now();
    if (msUntilExpiry <= 0) {
      return undefined;
    }
    const expiryTimer = setTimeout(() => setNow(Date.now()), msUntilExpiry + 100);
    const countdownInterval = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => {
      clearTimeout(expiryTimer);
      clearInterval(countdownInterval);
    };
  }, [activeBanner]);

  const timeRemaining = activeBanner ? formatTimeRemaining(activeBanner.expiryDate, now) : null;

  const isTitleTooLong = title.length > MAX_TITLE_LENGTH;
  const isMessageTooLong = message.length > MAX_MESSAGE_LENGTH;
  const hasUnresolvedTemplateTokens = TEMPLATE_TOKEN_PATTERN.test(message);

  const handlePublish = () => {
    setIsLoading(true);
    createBanner(title, message, severity, expiryDate)
      .then(() => {
        setResultDialog('Banner published successfully');
        setTitle('');
        setMessage('');
        setDurationAmount('');
        setCustomDate('');
        setShowPreview(false);
        refreshActiveBanner();
      })
      .catch((err) => setResultDialog(err.message || 'Failed to publish banner'))
      .finally(() => setIsLoading(false));
  };

  const handleExtend = () => {
    if (!activeBanner) {
      return;
    }
    setIsLoading(true);
    extendBanner(activeBanner.id, extendExpiryDate)
      .then(() => {
        setResultDialog('Banner extended successfully');
        setExtendDurationAmount('');
        setExtendCustomDate('');
        refreshActiveBanner();
      })
      .catch(() => setResultDialog('Failed to extend banner'))
      .finally(() => setIsLoading(false));
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setTitle(template.label);
    setMessage(template.message);
    setSeverity(template.severity);
    setShowTemplatePicker(false);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    setIsLoading(true);
    deleteBanner()
      .then(() => {
        setResultDialog('Banner deleted successfully');
        refreshActiveBanner();
      })
      .catch(() => setResultDialog('Failed to delete banner'))
      .finally(() => setIsLoading(false));
  };

  return (
    <div>
      <InformationDialog
        isOpen={resultDialog !== null}
        title="Manage Banner"
        message={resultDialog || ''}
        variant="information"
        handleConfirmConfirmationDialog={() => setResultDialog(null)}
      />
      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Banner"
        message="Are you sure you want to delete the active banner? It will stop showing for all users immediately."
        variant="destructive"
        confirmLabel="Delete"
        handleConfirmConfirmationDialog={handleDelete}
        handleDismissConfirmationDialog={() => setShowDeleteConfirm(false)}
      />
      <TemplatePickerDialog
        isOpen={showTemplatePicker}
        templates={MESSAGE_TEMPLATES}
        onSelect={handleTemplateSelect}
        onDismiss={() => setShowTemplatePicker(false)}
      />

      <Heading variant="subtitle1">Manage Maintenance Banner</Heading>

      {activeBanner ? (
        <div className="manage-banner-active-section">
          <Heading variant="subtitle2">
            {isBannerExpired(activeBanner, now) ? 'Last Banner (Expired — not shown to users)' : 'Currently Active Banner'}
          </Heading>
          {activeBanner.title && <p><b>Title:</b> {activeBanner.title}</p>}
          <p className="manage-banner-active-message"><b>Message:</b> {activeBanner.message}</p>
          <p>
            <b>Severity:</b> <SeverityBadge severity={activeBanner.severity} />
          </p>
          <p>
            <b>Expires:</b> {activeBanner.expiryDate}
            {timeRemaining && <span className="manage-banner-expiry-badge">{timeRemaining}</span>}
          </p>
          <p><b>Created by:</b> {activeBanner.createdBy}</p>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <b>
                New Expiry (
                {isBannerExpired(activeBanner, now)
                  ? 'extends from now, since the current banner has already expired'
                  : 'extends from current expiry, not from creation time'}
                )
              </b>
              <ExpiryPicker
                mode={extendMode}
                setMode={setExtendMode}
                durationAmount={extendDurationAmount}
                setDurationAmount={setExtendDurationAmount}
                durationUnit={extendDurationUnit}
                setDurationUnit={setExtendDurationUnit}
                customDate={extendCustomDate}
                setCustomDate={setExtendCustomDate}
                idPrefix="extend"
              />
              <br />
              <Button variant="cta" disabled={!extendExpiryDate || isLoading} onClick={handleExtend}>
                Extend
              </Button>
            </Grid>
            <Grid item md={4} xs={12}>
              <div className="manage-banner-danger-zone">
                <b>Remove Banner</b>
                <p className="manage-banner-danger-caption">
                  {isBannerExpired(activeBanner, now)
                    ? 'Clears this expired banner record; it is already not shown to users.'
                    : 'Stops showing it to all users immediately.'}
                </p>
                <Button variant="negative" disabled={isLoading} onClick={() => setShowDeleteConfirm(true)}>
                  Delete Banner
                </Button>
              </div>
            </Grid>
          </Grid>
        </div>
      ) : (
        <p>No banner is currently active.</p>
      )}

      <div className="manage-banner-publish-section">
        <Heading variant="subtitle2">Publish New Banner</Heading>
        <Grid container spacing={3}>
          <Grid item md={4} xs={12}>
            <div className="manage-banner-message-header">
              <b>Title</b>
              <Button variant="secondary" onClick={() => setShowTemplatePicker(true)}>
                Insert Template
              </Button>
            </div>
            <p className="manage-banner-char-count">Fills in the title, message, and severity below from a preset.</p>
            <TextField
              className="manage-banner-title-textfield"
              value={title}
              placeholder="Short bold headline shown at the top of the banner"
              onChange={setTitle}
            />
          <p className={isTitleTooLong ? 'manage-banner-char-count-error' : 'manage-banner-char-count'}>
            {title.length} / {MAX_TITLE_LENGTH}
            {isTitleTooLong && ` — title is too long, remove ${title.length - MAX_TITLE_LENGTH} characters`}
          </p>
          <b>Message</b>
          <TextField
            multiLine
            className="manage-banner-message-textfield"
            value={message}
            placeholder="Enter the maintenance/outage message shown to all users"
            onChange={setMessage}
          />
          <p className={isMessageTooLong ? 'manage-banner-char-count-error' : 'manage-banner-char-count'}>
            {message.length} / {MAX_MESSAGE_LENGTH}
            {isMessageTooLong && ` — message is too long, remove ${message.length - MAX_MESSAGE_LENGTH} characters`}
          </p>
          {hasUnresolvedTemplateTokens && (
            <p className="manage-banner-char-count-error">
              Message still contains a placeholder like [Start Time] — replace it with the actual value before publishing.
            </p>
          )}
        </Grid>
        <Grid item md={4} xs={12}>
          <b>Severity</b>
          <br />
          <Select
            aria-label="Banner Severity"
            defaultValue={severity}
            value={severity}
            options={SEVERITY_OPTIONS}
            onSelect={(value: BannerSeverity) => setSeverity(value)}
          />
          <br />
          <br />
          <b>Expiry</b>
          <ExpiryPicker
            mode={expiryMode}
            setMode={setExpiryMode}
            durationAmount={durationAmount}
            setDurationAmount={setDurationAmount}
            durationUnit={durationUnit}
            setDurationUnit={setDurationUnit}
            customDate={customDate}
            setCustomDate={setCustomDate}
            idPrefix="publish"
          />
        </Grid>
        </Grid>
        <div className="manage-banner-actions">
          <Button
            variant="secondary"
            disabled={!title || !message}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? 'Hide Preview' : 'Preview'}
          </Button>
          <Button
            variant="cta"
            disabled={!title || !message || !expiryDate || isLoading || isTitleTooLong || isMessageTooLong || hasUnresolvedTemplateTokens}
            onClick={handlePublish}
          >
            Publish Banner
          </Button>
          {(!title || !message || !expiryDate) && (
            <span className="manage-banner-char-count">
              {describeDisabledPublishReason(Boolean(title), Boolean(message), Boolean(expiryDate))}
            </span>
          )}
        </div>
        {showPreview && (
          <div className="manage-banner-preview">
            <BannerContent title={title} message={message} severity={severity} />
          </div>
        )}
      </div>
    </div>
  );
}

interface ManageBannerSubTab {
  label: string;
  renderContent: () => React.ReactElement;
}

const MANAGE_BANNER_SUB_TABS: ManageBannerSubTab[] = [
  { label: 'Publish / Manage', renderContent: () => <PublishBannerPage /> },
  { label: 'Admin Access', renderContent: () => <ManageBannerAdminsPage /> }
];

function ManageBannerPage(): React.ReactElement {
  const [subTabIndex, setSubTabIndex] = React.useState(0);

  return (
    <div>
      <TabView selectedIndex={subTabIndex} onSelect={setSubTabIndex}>
        {MANAGE_BANNER_SUB_TABS.map((tabItem) => (
          <Tab key={tabItem.label} label={tabItem.label} />
        ))}
      </TabView>
      {MANAGE_BANNER_SUB_TABS[subTabIndex].renderContent()}
    </div>
  );
}

export default ManageBannerPage;
